import React from "react";
import { api } from "../lib/api";

/**
 * Global consult dialog.
 * Opens when window.location.hash === "#consult" (any suffix ok, e.g. "#consult-pkg-1").
 * Reads optional package name from window.__askConsultPackage set by triggers.
 */
export default function ConsultDialog() {
  const [open, setOpen] = React.useState(false);
  const [pkg, setPkg] = React.useState("Book a Consult");
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [business, setBusiness] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [status, setStatus] = React.useState("idle");
  const [error, setError] = React.useState("");

  const close = React.useCallback(() => {
    if (window.location.hash.startsWith("#consult")) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    setOpen(false);
    setStatus("idle");
    setError("");
  }, []);

  const openFromHash = React.useCallback(() => {
    const h = window.location.hash;
    if (h.startsWith("#consult")) {
      const label = window.__askConsultPackage || "Book a Consult";
      setPkg(label);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, []);

  React.useEffect(() => {
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [openFromHash]);

  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) return;
    setStatus("loading");
    setError("");
    try {
      await api.post("/consult", { name, email, phone, business, package: pkg, message });
      setStatus("success");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  if (!open) return null;

  const inputClass =
    "w-full bg-transparent border-b-2 border-[#1F1B18]/40 py-3 font-cormorant text-2xl sm:text-3xl text-[#1F1B18] focus:outline-none focus:border-[#D9739A] transition";
  const labelClass =
    "font-typewriter text-xs sm:text-sm font-bold tracking-[0.32em] uppercase text-[#1F1B18]/75 block mb-2";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center px-3 py-4 sm:px-6 sm:py-8 overflow-y-auto"
      data-testid="consult-dialog"
      role="dialog"
      aria-modal="true"
    >
      <button
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={close}
        aria-label="Close"
        data-testid="consult-dialog-overlay"
      />

      <div className="relative w-full max-w-2xl bg-[#F8F2E8] border border-[#1F1B18]/20 shadow-2xl my-auto">
        <button
          onClick={close}
          data-testid="consult-dialog-close"
          className="absolute top-4 right-5 font-typewriter text-xs sm:text-sm font-bold tracking-[0.3em] uppercase text-[#1F1B18]/75 hover:text-[#D9739A] transition"
        >
          close ×
        </button>

        <div className="p-8 sm:p-12">
          <p className="font-typewriter text-xs sm:text-sm font-bold tracking-[0.4em] uppercase text-[#8B6B4A]">
            {pkg}
          </p>
          <h2 className="font-cormorant text-4xl sm:text-5xl leading-tight mt-4 text-[#1F1B18]">
            Let's have a <span className="italic text-[#D9739A]">conversation.</span>
          </h2>
          <p className="font-cormorant italic text-xl sm:text-2xl text-[#1F1B18]/75 mt-3">
            Share a little about you — Kimberley will reply personally.
          </p>

          {status === "success" ? (
            <div className="mt-10 text-center py-10" data-testid="consult-dialog-success">
              <p className="font-caveat text-6xl sm:text-7xl text-[#D9739A]">Thank you.</p>
              <p className="font-cormorant text-2xl text-[#1F1B18]/80 mt-6">
                Your message is on its way. Kimberley will be in touch soon.
              </p>
              <button
                onClick={close}
                className="mt-10 inline-flex items-center justify-center border border-[#1F1B18] text-[#1F1B18] px-10 py-4 font-typewriter text-sm font-bold tracking-[0.3em] uppercase hover:bg-[#1F1B18] hover:text-[#F8F2E8] transition"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-6" data-testid="consult-dialog-form">
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="consult-name"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Email *</label>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="consult-email"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Phone (optional)</label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 512 555 1234"
                  data-testid="consult-phone"
                  className={`${inputClass} placeholder-[#1F1B18]/30`}
                />
              </div>
              <div>
                <label className={labelClass}>Business (optional)</label>
                <input
                  value={business}
                  onChange={(e) => setBusiness(e.target.value)}
                  data-testid="consult-business"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Tell Kimberley a little (optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="consult-message"
                  rows={4}
                  className="w-full bg-transparent border-2 border-[#1F1B18]/30 p-4 font-cormorant text-xl sm:text-2xl text-[#1F1B18] focus:outline-none focus:border-[#D9739A] transition resize-none"
                />
              </div>

              {error && (
                <p className="font-cormorant italic text-lg text-red-700" data-testid="consult-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="consult-submit"
                className="w-full inline-flex items-center justify-center bg-[#1F1B18] text-[#F8F2E8] border border-[#1F1B18] px-10 py-5 font-typewriter text-sm font-bold tracking-[0.3em] uppercase hover:bg-[#D9739A] hover:border-[#D9739A] transition disabled:opacity-50"
              >
                {status === "loading" ? "Sending…" : "Send Enquiry →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
